import _ from 'lodash';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import type { CanvasItemLayoutDescDictionary } from '@UI/Canvas/Canvas';
import type { DateFormat, TimeFormat } from '@Utils/Formats';
import type { SelectOptions } from '@Primitive/Select';
import type { IRadioButtons } from '../Control/CommonList';
import type { ColorPaletteMode } from './ColorPalette';
// =======================================================================================================
export const enum FormZoomPolicy {
    FillWidth,
    FillHeight,
    AvoidScrollbar
}
// ------------------------------------------------------------------------------------------------------
export interface FormData {
    FormSettings: FormSettings;
    canvasLayoutDesc: CanvasItemLayoutDescDictionary;
    GroupCanvasLayoutDesc: { [key: string]: CanvasItemLayoutDescDictionary };
    /**
     * Width threshold in pixels in which we define if canvas or list layout will be used.
     */
    layoutThres: number;
}
// ------------------------------------------------------------------------------------------------------
export interface ISelectData {
    JsonName: string;
    Key: string;
    data: SelectOptions;
    multi: boolean;
    creatable: boolean;
    closeMenuOnSelect: boolean;
}
// ------------------------------------------------------------------------------------------------------
export interface FormSettings {
    // Groups
    Groups: FormGroupSettings[];
    GroupLayout: FormGroupLayout;

    GroupListColapsable: boolean;
    GroupListOnlyOneOpen: boolean;

    Elements: FormElementSettings[];
    Format: DateFormat;
    TimeFormat: TimeFormat;
    SelectData: ISelectData[];
    RadioButton: IRadioButtons[];
    Buttons: ButtonSettings[];
    ColorPaletteMode: ColorPaletteMode;
    BorderRadius: number;
    FormZoomPolicy: FormZoomPolicy;
    Colors: {
        Mandatory: string;
        Default: string;
        ReadOnly: string;
        InputField: string;
        Placeholder: string;
        FocusedUnderline: string;
    };
    Image: ImageSettings[];
    BoldHeaderText: boolean;
}
// ------------------------------------------------------------------------------------------------------
export interface FormGroupSettings {
    UserKey: string;
    Key: string;
    Label: string;
    Icon: string;
    FontColor: string;
    BackgroundColor: string;
}
// ------------------------------------------------------------------------------------------------------
export const enum FormGroupLayout {
    Tabs = 0,
    List = 1
}
// ------------------------------------------------------------------------------------------------------
export const enum DependencyType {
    Equal = 0,
    Higher = 1,
    Lower = 2,
    HasValue = 3,
    NotEqual = 4,
}
// ------------------------------------------------------------------------------------------------------
export interface Dependency {
    JsonID: string;
    Type: DependencyType;
    Value?: any;
}
// ------------------------------------------------------------------------------------------------------
export interface FormElementSettings {
    /**
     * Link the element to specific group
     */
    GroupKey: string;
    /**
     * Random number for identification
     */
    Key: string;
    JsonName: string;
    TooltipLabel: string;
    Label: string;
    Type: ElementType;
    Placeholder: string;
    Mandatory: boolean;
    ReadOnly: boolean;
    /**
     * Validation error message (e.g.: wrong e-mail format)
     */
    ValidationError: boolean;
    DuplicateJsonName: boolean;
    Dependency: Dependency[];
    IsHidden: boolean;
    DefaultValue: any;
}
// ------------------------------------------------------------------------------------------------------
/**
 * Used from Timeline too!
 */
export interface ButtonSettings {
    Key: string;
    Label: string;
    Type: ButtonThemeType;
    Flat: boolean;
    Uppercase: boolean;
    Tooltip?: string;

    /**
     * Used only if button's type is custom
     */
    Color: string;
    FontColor: string;
    /**
     * Send data without the mandatory check.
     */
    ForceSend?: boolean;
    Dependency?: Dependency[];
}
// ------------------------------------------------------------------------------------------------------
export interface ImageSettings {
    Key: string;
    AutomaticUpload: boolean;
    MaxSize: number;
    AutoDeletion: AutoDeletion;
    Completed: number;
    ErrorText: string;
    Transformation: ITransformation;
}
// ------------------------------------------------------------------------------------------------------
export interface ITransformation {
    MaxWidth: number;
    MaxHeight: number;
    Format: IFormat;
    Quality: number;
}
// ------------------------------------------------------------------------------------------------------
export const enum IFormat {
    PNG = 'png',
    GIF = 'gif',
    BMP = 'bmp',
    JPEG = 'jpeg',
    WEBP = 'webp'
}
// ------------------------------------------------------------------------------------------------------
export interface AutoDeletion {
    Type: AutoDeletionType;
    AutoDelete: boolean;
    Downloads: number;
    Days: number;
}
// ------------------------------------------------------------------------------------------------------
export const enum AutoDeletionType {
    Days = 0,
    Downloads = 1
}
// ------------------------------------------------------------------------------------------------------
export interface FormValues {
    JsonName: string;
    Value: string;
}
// ------------------------------------------------------------------------------------------------------
export interface FormState {
    WidgetData: FormData;
    FormValues: { [key: string]: string };
    ColorPickersVis: { [key: string]: boolean };
    groupTabIndex: number;
    groupListOpenList: string[];
}
// ------------------------------------------------------------------------------------------------------
export enum ElementType {
    Text = 0,
    Email = 1,
    Password = 2,
    Select = 3,
    DateRangePicker = 4,
    Boolean = 5,
    Color = 6,
    Number = 7,
    TextArea = 8,
    DatePicker = 9,
    DateTimePicker = 10,
    Markdown = 11,
    RadioButtons = 12,
    DateTimeRangePicker = 13,
    TimeRangePicker = 14,
    TimePicker = 15,
    Image = 16,
    StaticImage = 17
}
// ------------------------------------------------------------------------------------------------------
export function ElementTypeToString(element: ElementType) {
    switch (element) {
        case ElementType.Text: return "Text";
        case ElementType.TextArea: return "Text Area";
        case ElementType.Number: return "Number";
        case ElementType.Email: return "E-mail";
        case ElementType.Password: return "Password";
        case ElementType.Select: return "Select";
        case ElementType.DateRangePicker: return "Date Range Picker";
        case ElementType.DateTimeRangePicker: return "Date Time Range Picker";
        case ElementType.TimeRangePicker: return "Time Range Picker";
        case ElementType.DatePicker: return "Date Picker";
        case ElementType.TimePicker: return "Time Picker";
        case ElementType.DateTimePicker: return "Date Time Picker";
        case ElementType.Color: return "Color Picker";
        case ElementType.Boolean: return "Boolean";
        case ElementType.Markdown: return "Markdown";
        case ElementType.RadioButtons: return "Radio Buttons";
    }
    return "";
}
// ------------------------------------------------------------------------------------------------------
export enum ButtonThemeType {
    Default = 0,
    Primary = 1,
    Secondary = 2,
    Custom = 3
}
// ------------------------------------------------------------------------------------------------------
export function FormOptionsJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("FormOptionsJsonSchemeValidator", {
        title: "SelectDataType",
        type: "array",
        items: {
            type: "object",
            properties: {
                label: { type: "string" },
                value: { type: ["string", "number", "boolean"] },
            },
            required: ["label", "value"]
        }
    });
}
// ------------------------------------------------------------------------------------------------------
export const enum FormPortKeys {
    fillValues = 'fillvalues',
    onSelectChange = 'onselectchange'
}
// ------------------------------------------------------------------------------------------------------
export function _getLastYLayoutPosition(layoutDesc: CanvasItemLayoutDescDictionary): number {
    let lastY: number = 0;

    // Find last y layout's position.
    _.forOwn(layoutDesc, (layout) => {
        if (layout.Y + layout.H > lastY) {
            lastY = layout.Y + layout.H;
        }
    });

    return lastY;
}
// ------------------------------------------------------------------------------------------------------